import React, { useEffect, useState } from 'react'
import { Header } from '@st-operations/uikit/src/v2'
import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1'
import Select from '_components/select'
import { getFeatures, createFeature, deleteFeature } from '_services/predactiv-admin'
import Table from '../table'
import { ENV_OPTIONS, DEFAULT_ENV } from '../helpers'
import User from '_lib/User.js'
import {
  Button,
  BUTTON_THEME,
  BUTTON_COLOR,
  BarLoader,
  Input,
  IconButton,
} from '@st-operations/uikit/src/v2/index.js'
import './styles.scss'

const DeleteFeatureModal = ({ feature, onClose, onConfirm }) => {
  const handleDelete = () => {
    onConfirm(feature._id)
    setTimeout(onClose, 200)
  }

  return (
    <Dialog open={true} onClose={onClose}>
      <h2 className="text-center text-lg font-bold mb-4">Are you sure?</h2>
      <div className="mb-4">
        You are deleting the feature flag <b>{feature.label}</b>.
      </div>
      <div className="flex justify-end gap-2">
        <Button onClick={onClose} theme={BUTTON_THEME.GHOST}>
          Cancel
        </Button>
        <Button onClick={handleDelete} color={BUTTON_COLOR.RED}>
          Continue
        </Button>
      </div>
    </Dialog>
  )
}

const TYPE_OPTIONS = [
  { label: 'General', value: 'GENERAL' },
  { label: 'Insights', value: 'INSIGHTS' },
  { label: 'Filters', value: 'FILTERS' },
]

const VALUES_INITIAL_STATE = {
  label: '',
  name: '',
  type: TYPE_OPTIONS[0],
}

function snake_case(str) {
  return str
    .split(' ')
    .map((word) => {
      return word.toLowerCase()
    })
    .join('_')
}

const FeatureFlags = () => {
  const [env, setEnv] = useState(DEFAULT_ENV)
  const [features, setFeatures] = useState([])
  const [values, setValues] = useState(VALUES_INITIAL_STATE)
  const [isLoading, setIsLoading] = useState(false)
  const [featureToDelete, setFeatureToDelete] = useState(null)

  useEffect(() => {
    setIsLoading(true)
    getFeatures(env.value)
      .then((data) => setFeatures(data.items))
      .finally(() => setIsLoading(false))
  }, [env])

  const handleCreate = async () => {
    setIsLoading(true)
    await createFeature({
      env: env.value,
      ...values,
      type: values.type.value,
    })

    setValues(VALUES_INITIAL_STATE)
    getFeatures(env.value)
      .then((data) => setFeatures(data.items))
      .finally(() => setIsLoading(false))
  }

  const handleDelete = async (id) => {
    setIsLoading(true)
    try {
      await deleteFeature({
        env: env.value,
        id,
      })
      getFeatures(env.value)
        .then((data) => setFeatures(data.items))
        .finally(() => setIsLoading(false))
    } catch (error) {
      console.error('Error deleting feature:', error)
      toast.error('Failed to delete feature')
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setValues((prev) => ({
      ...prev,
      name: snake_case(values.label),
    }))
  }, [values.label])

  const renderAction = (data) =>
    env.value !== 'prod' ? (
      <IconButton
        onClick={() => setFeatureToDelete(data)}
        aria-label="Delete"
        iconProps={{
          icon: 'trash',
          type: 'outline',
          width: 16,
          height: 16,
          color: '#3D3C68',
        }}
      />
    ) : null

  const permissions = User?.data?.datasphere_permissions?.predactiv?.permissions || {}

  if (!permissions.features?.display) {
    return null
  }

  return (
    <div>
      <Header
        title={
          <div className="predactiv-admin__header">
            <span>Predactiv Feature Flags</span>
            <Select value={env} options={ENV_OPTIONS} onChange={(value) => setEnv(value)} />
          </div>
        }
      />
      <div className="predactiv-features__form">
        <Input
          label="Label"
          value={values.label}
          onChange={(value) => setValues({ ...values, label: value })}
        />
        <Input
          label="Name"
          value={values.name}
          onChange={(value) => setValues({ ...values, name: value })}
        />
        <Select
          className=""
          label="Type"
          value={values.type}
          options={TYPE_OPTIONS}
          onChange={(value) => setValues({ ...values, type: value })}
        />
        <Button theme={BUTTON_THEME.PRIMARY} className="mx-0" onClick={handleCreate}>
          Add
        </Button>
      </div>
      <If condition={isLoading}>
        <BarLoader />
      </If>
      <If condition={!isLoading}>
        <Table
          gridTemplateColumns="repeat(3, minmax(0px, 1fr)) auto"
          header={['Label', 'Name', 'Type']}
          items={features?.map((item) => ({
            data: item,
            values: [
              item.label,
              item.name,
              Object.values(TYPE_OPTIONS).find((type) => type.value === item.type)?.label ||
                item.type,
            ],
          }))}
          renderAction={renderAction}
        />
      </If>
      <If condition={featureToDelete}>
        <DeleteFeatureModal
          feature={featureToDelete}
          onClose={() => setFeatureToDelete(null)}
          onConfirm={handleDelete}
        />
      </If>
    </div>
  )
}

export default FeatureFlags
